import type { GpsPeerSource } from '../types';
import type { NearbyPeer } from '../types';
import { peerIdMatches } from './peerId';
import { getBleGpsPeers } from './blePeerGps';
import {
  combineHorizontalAccuracyMeters,
  formatFindPeerDistanceWithWalkTime,
} from './findDistanceFormat';
import { smoothBearing, smoothDistance, smoothUwbDistance } from './displaySmoothing';
import { getRemoteGpsPeers } from './peerGpsStore';
import { formatStalenessShort } from './staleness';

/** GPS position older than this shows a trust warning (arrow still shown for stale). */
export const GPS_TRUST_WARNING_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * GPS older than this is shown as stale (no RSSI fallback). Two and a half heartbeats: a still phone
 * publishes every 60 s, so a 60 s bar flipped every seated friend to stale for a moment each minute
 * (Bowl + Greek audit, 2026-10-12).
 */
export const GPS_STALE_THRESHOLD_MS = 150_000;

/**
 * Radar placement fallback when a peer has no usable distance at all (a BLE-nearby peer with no band, a bad row).
 * A banded BLE peer is drawn at its band distance instead (radarPlacementMeters, 2026-10-16 audit).
 */
export const BLE_IN_RANGE_PLACEHOLDER_METERS = 55;

const GPS_PEER_SOURCES = new Set<GpsPeerSource>(['remote', 'ble-gps']);

export type PeerPresenceKind = 'uwb-live' | 'gps-live' | 'gps-stale' | 'ble-in-range' | 'none';

export type PeerNameResolver = (peerId: string) => string;

export const defaultPeerNameResolver: PeerNameResolver = () => 'Group member';

export interface PeerDisplayInfo {
  kind: PeerPresenceKind;
  displayName: string;
  /**
   * Smoothed GPS distance for gps-live / gps-stale, the UWB range for uwb-live, and for ble-in-range the RSSI band's
   * outer distance (BLE_RSSI_BANDS) - a "within", never a position, and never paired with a bearing.
   */
  distanceMeters: number | null;
  listPreview: string | null;
  sortTier: number;
  sortDistance: number;
  gpsLastKnownAt: number | null;
  horizontalAccuracyMeters: number | null;
  /** The bearing to draw: the UWB one (live or held with its range), else the smoothed GPS one; null when neither. */
  bearingDeg: number | null;
  bearingSource: 'uwb' | 'gps' | null;
  /**
   * How far off the bearing could be, in degrees: 0 for a UWB direction, atan(fix error / shown distance) for GPS,
   * null when the error is unknown. Two phones 5 m apart with 12 m fixes have no usable direction (car, 2026-10-14).
   */
  bearingUncertaintyDeg: number | null;
}

function isValidTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function normalizeGpsSource(source: GpsPeerSource | undefined): GpsPeerSource | undefined {
  if (source === undefined || !GPS_PEER_SOURCES.has(source)) {
    return undefined;
  }
  return source;
}

function hasFiniteUwb(peer: NearbyPeer): boolean {
  return (
    peer.uwbActive === true && peer.uwbDistance !== undefined && Number.isFinite(peer.uwbDistance)
  );
}

/**
 * After a UWB range drops, the radar keeps it this long before falling back to GPS - the same hold the map uses
 * (peerPlacement UWB_HOLD_MS). Without it a blink in the NI session swung a friend from 1 m to a 6 m GPS fix
 * and back, the in-and-out jitter on the Find page at the Greek (2026-10-11).
 */
export const UWB_DISPLAY_HOLD_MS = 4000;

interface UwbHeld {
  distanceMeters: number;
  rangeAt: number;
  bearingDeg: number | null;
  bearingAt: number;
}

const lastUwbByPeer = new Map<string, UwbHeld>();

function finiteUwbBearing(peer: NearbyPeer): number | null {
  return peer.uwbBearing != null && Number.isFinite(peer.uwbBearing) ? peer.uwbBearing : null;
}

/**
 * The UWB range to show: live when there is one, the last one inside the hold, otherwise null. The bearing is held
 * the same way, from when it was last seen: the engine drops uwbBearing the moment the session blinks, and a held
 * range drawn on the GPS bearing swung the friend round the radar for the length of the hold (2026-10-14).
 */
function uwbToShow(
  peer: NearbyPeer,
  now: number,
): { distanceMeters: number; bearingDeg: number | null } | null {
  const last = lastUwbByPeer.get(peer.peerId);
  const heldBearing = (): number | null =>
    last && last.bearingDeg != null && now - last.bearingAt < UWB_DISPLAY_HOLD_MS
      ? last.bearingDeg
      : null;
  if (hasFiniteUwb(peer)) {
    const distanceMeters = smoothUwbDistance(peer.peerId, peer.uwbDistance!, now);
    const liveBearing = finiteUwbBearing(peer);
    const next: UwbHeld =
      liveBearing != null
        ? { distanceMeters, rangeAt: now, bearingDeg: liveBearing, bearingAt: now }
        : {
            distanceMeters,
            rangeAt: now,
            bearingDeg: heldBearing(),
            bearingAt: last?.bearingAt ?? now,
          };
    lastUwbByPeer.set(peer.peerId, next);
    return { distanceMeters, bearingDeg: next.bearingDeg };
  }
  if (last && now - last.rangeAt < UWB_DISPLAY_HOLD_MS) {
    return { distanceMeters: last.distanceMeters, bearingDeg: heldBearing() };
  }
  lastUwbByPeer.delete(peer.peerId);
  return null;
}

/** GPS bearing to the peer, smoothed the shortest way round; null without one. */
function smoothedGpsBearing(peer: NearbyPeer, now: number): number | null {
  if (peer.gpsBearingDeg == null || !Number.isFinite(peer.gpsBearingDeg)) {
    return null;
  }
  return smoothBearing(peer.peerId, peer.gpsBearingDeg, now);
}

/** Degrees the GPS bearing could be off: the combined fix error seen from the shown distance. */
export function gpsBearingUncertaintyDeg(
  horizontalAccuracyMeters: number | null,
  distanceMeters: number | null,
): number | null {
  if (horizontalAccuracyMeters == null || !Number.isFinite(horizontalAccuracyMeters)) {
    return null;
  }
  if (distanceMeters == null || !Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    return 90;
  }
  return (Math.atan(Math.max(0, horizontalAccuracyMeters) / distanceMeters) * 180) / Math.PI;
}

/** Tests and session teardown. */
export function resetUwbDisplayHold(): void {
  lastUwbByPeer.clear();
}

/** Tracking ended for this peer: a held range must not outlive it (the hold is for blinks, not for goodbyes). */
export function forgetUwbDisplayHold(peerId: string): void {
  lastUwbByPeer.delete(peerId);
}

function hasFiniteGps(peer: NearbyPeer): boolean {
  return peer.gpsDistance !== undefined && Number.isFinite(peer.gpsDistance);
}

function gpsAccuracyMetersForPeer(
  peerId: string,
  gpsSource: GpsPeerSource | undefined,
): number | null {
  const source = normalizeGpsSource(gpsSource);
  if (source === 'remote') {
    const accuracy = getRemoteGpsPeers().find((p) => peerIdMatches(p.id, peerId))?.accuracyMeters;
    return accuracy != null && Number.isFinite(accuracy) && accuracy > 0 ? accuracy : null;
  }
  if (source === 'ble-gps') {
    const accuracy = getBleGpsPeers().find((p) => peerIdMatches(p.id, peerId))?.accuracyMeters;
    return accuracy != null && Number.isFinite(accuracy) && accuracy > 0 ? accuracy : null;
  }
  return null;
}

function gpsLastKnownAtForPeer(
  peerId: string,
  gpsSource: GpsPeerSource | undefined,
): number | null {
  const source = normalizeGpsSource(gpsSource);
  if (source === 'remote') {
    const ts = getRemoteGpsPeers().find((p) => peerIdMatches(p.id, peerId))?.lastKnownAt;
    return isValidTimestamp(ts) ? ts : null;
  }
  if (source === 'ble-gps') {
    const ts = getBleGpsPeers().find((p) => peerIdMatches(p.id, peerId))?.lastKnownAt;
    return isValidTimestamp(ts) ? ts : null;
  }
  return null;
}

/**
 * Bluetooth "nearby": a friend whose phone we hear but cannot range. Entering takes a sighting this fresh; once in,
 * they stay until Bluetooth has been silent for BLE_NEARBY_HOLD_MS, so a scan gap does not flip the row back to a
 * stale GPS fix and forward again.
 */
export const BLE_NEARBY_ENTER_MS = 10_000;
export const BLE_NEARBY_HOLD_MS = 30_000;

/**
 * RSSI bands, by smoothed dBm, closest first. Phone-to-phone RSSI through bodies and pockets is good to a band,
 * not to a metre, so the label says "within" and there is never a bearing. Moving to another band takes
 * BLE_BAND_HYSTERESIS_DB past the edge.
 */
export const BLE_RSSI_BANDS: ReadonlyArray<{ minDbm: number; meters: number; label: string }> = [
  { minDbm: -62, meters: 2, label: 'Right here' },
  { minDbm: -72, meters: 6, label: 'Within 20 ft' },
  { minDbm: -82, meters: 15, label: 'Within 50 ft' },
  { minDbm: Number.NEGATIVE_INFINITY, meters: 30, label: 'Nearby' },
];
export const BLE_BAND_HYSTERESIS_DB = 4;

const bleNearbyByPeer = new Map<string, { band: number }>();

function rawBandIndex(rssi: number): number {
  return BLE_RSSI_BANDS.findIndex((band) => rssi >= band.minDbm);
}

/** The band to show for this RSSI, holding the previous band until the reading is clearly past its edge. */
function bandWithHysteresis(rssi: number, previous: number | undefined): number {
  const raw = rawBandIndex(rssi);
  if (previous === undefined || raw === previous) {
    return raw;
  }
  if (raw < previous) {
    // Closer: must clear the edge of the band above ours by the margin.
    return rssi >= BLE_RSSI_BANDS[previous - 1].minDbm + BLE_BAND_HYSTERESIS_DB ? raw : previous;
  }
  // Further: must drop below our own band's floor by the margin.
  return rssi < BLE_RSSI_BANDS[previous].minDbm - BLE_BAND_HYSTERESIS_DB ? raw : previous;
}

function bleRssi(peer: NearbyPeer): number | null {
  const value = Number.isFinite(peer.smoothedRssi) ? peer.smoothedRssi : peer.rssi;
  return Number.isFinite(value) && value < 0 ? value : null;
}

/** RSSI-band distance while the peer counts as Bluetooth-nearby, else null (and their band is forgotten). */
function bleNearbyMeters(peer: NearbyPeer, now: number): number | null {
  const rssi = bleRssi(peer);
  const ageMs = isValidTimestamp(peer.lastSeenAt) ? now - peer.lastSeenAt : Number.NaN;
  const previous = bleNearbyByPeer.get(peer.peerId);
  const windowMs = previous ? BLE_NEARBY_HOLD_MS : BLE_NEARBY_ENTER_MS;
  if (rssi === null || !Number.isFinite(ageMs) || ageMs < 0 || ageMs >= windowMs) {
    bleNearbyByPeer.delete(peer.peerId);
    return null;
  }
  const band = bandWithHysteresis(rssi, previous?.band);
  bleNearbyByPeer.set(peer.peerId, { band });
  return BLE_RSSI_BANDS[band].meters;
}

/** Tests and session teardown. */
export function resetBleNearbyHold(): void {
  bleNearbyByPeer.clear();
}

function listPreviewForKind(
  kind: PeerPresenceKind,
  peerId: string,
  distanceMeters: number | null,
  lastKnownAt: number | null,
  now: number,
  horizontalAccuracyMeters: number | null,
): string | null {
  const safeLastKnownAt = isValidTimestamp(lastKnownAt) ? lastKnownAt : null;
  const formatOptions = {
    peerId,
    horizontalAccuracyMeters,
    uwbPrecise: kind === 'uwb-live',
  };
  switch (kind) {
    case 'uwb-live': {
      if (distanceMeters === null || !Number.isFinite(distanceMeters)) {
        return null;
      }
      return `${formatFindPeerDistanceWithWalkTime(distanceMeters, formatOptions)} · now`;
    }
    case 'gps-live': {
      // The readout always carries a time (`now`, `1m`, `12m ago`), never distance alone — the Map page's
      // selected-person row and the Find row say the same thing.
      if (distanceMeters === null || !Number.isFinite(distanceMeters)) {
        return null;
      }
      return `${formatFindPeerDistanceWithWalkTime(distanceMeters, formatOptions)} · now`;
    }
    case 'gps-stale': {
      if (distanceMeters === null || !Number.isFinite(distanceMeters)) {
        return safeLastKnownAt !== null ? formatStalenessShort(safeLastKnownAt, now) : 'stale';
      }
      const distStr = formatFindPeerDistanceWithWalkTime(distanceMeters, formatOptions);
      const ageStr =
        safeLastKnownAt !== null ? formatStalenessShort(safeLastKnownAt, now) : 'stale';
      return `${distStr} · ${ageStr}`;
    }
    case 'ble-in-range':
      return bleNearbyLabel(distanceMeters);
    default:
      return null;
  }
}

/** The band's words for an RSSI-band distance ("Within 20 ft"); "Nearby" without one. */
export function bleNearbyLabel(distanceMeters: number | null | undefined): string {
  const band = BLE_RSSI_BANDS.find((b) => b.meters === distanceMeters);
  return band?.label ?? 'Nearby';
}

export function resolvePeerDisplay(
  peer: NearbyPeer,
  options: {
    now: number;
    gpsSource?: GpsPeerSource;
    smoothedGpsDistance?: number | null;
    nameResolver?: PeerNameResolver;
    selfAccuracyMeters?: number | null;
  },
): PeerDisplayInfo {
  const { smoothedGpsDistance } = options;
  const gpsSource = normalizeGpsSource(options.gpsSource ?? peer.gpsSource);
  const safeNow = isValidTimestamp(options.now) ? options.now : Date.now();
  const displayName = (options.nameResolver ?? defaultPeerNameResolver)(peer.peerId);
  const peerAccuracyMeters = gpsAccuracyMetersForPeer(peer.peerId, gpsSource);
  const horizontalAccuracyMeters = combineHorizontalAccuracyMeters(
    options.selfAccuracyMeters,
    peerAccuracyMeters,
  );

  // Kept warm under UWB too, so a hand-back to GPS starts from a settled bearing.
  const gpsBearingDeg = smoothedGpsBearing(peer, safeNow);
  const gpsBearing = (shownDistance: number | null) =>
    gpsBearingDeg === null
      ? { bearingDeg: null, bearingSource: null, bearingUncertaintyDeg: null }
      : {
          bearingDeg: gpsBearingDeg,
          bearingSource: 'gps' as const,
          bearingUncertaintyDeg: gpsBearingUncertaintyDeg(horizontalAccuracyMeters, shownDistance),
        };

  const uwb = uwbToShow(peer, safeNow);
  if (uwb !== null) {
    const distance = uwb.distanceMeters;
    // A UWB range takes UWB's direction or none - never the GPS bearing, which swung a 2 m friend across the radar.
    const bearing =
      uwb.bearingDeg !== null
        ? { bearingDeg: uwb.bearingDeg, bearingSource: 'uwb' as const, bearingUncertaintyDeg: 0 }
        : { bearingDeg: null, bearingSource: null, bearingUncertaintyDeg: null };
    // A UWB range is good to centimetres; the GPS accuracy floor (self + peer fix error) must not coarsen its
    // label to a 10 m step (2026-10-04).
    return {
      kind: 'uwb-live',
      displayName,
      distanceMeters: distance,
      listPreview: listPreviewForKind('uwb-live', peer.peerId, distance, null, safeNow, null),
      sortTier: 0,
      sortDistance: distance,
      gpsLastKnownAt: null,
      horizontalAccuracyMeters: null,
      ...bearing,
    };
  }

  // Computed before GPS so the band's hysteresis keeps running while a fresh fix is what shows.
  const bleMeters = bleNearbyMeters(peer, safeNow);
  const bleNearby: PeerDisplayInfo | null =
    bleMeters === null
      ? null
      : {
          kind: 'ble-in-range',
          displayName,
          distanceMeters: bleMeters,
          listPreview: listPreviewForKind(
            'ble-in-range',
            peer.peerId,
            bleMeters,
            null,
            safeNow,
            null,
          ),
          // After a live fix, ahead of a stale one: their phone is within Bluetooth range right now.
          sortTier: 1.5,
          sortDistance: bleMeters,
          gpsLastKnownAt: null,
          horizontalAccuracyMeters: null,
          // Bluetooth gives a distance band, never a direction.
          bearingDeg: null,
          bearingSource: null,
          bearingUncertaintyDeg: null,
        };

  if (hasFiniteGps(peer)) {
    const peerLastKnownAt = gpsLastKnownAtForPeer(peer.peerId, gpsSource);
    const fallbackLastSeen = isValidTimestamp(peer.lastSeenAt) ? peer.lastSeenAt : null;
    const gpsLastKnownAt = peerLastKnownAt ?? fallbackLastSeen;
    const isStale =
      gpsLastKnownAt === null ||
      !isValidTimestamp(gpsLastKnownAt) ||
      safeNow - gpsLastKnownAt > GPS_STALE_THRESHOLD_MS;
    const distance = smoothedGpsDistance ?? smoothDistance(peer.peerId, peer.gpsDistance!, safeNow);
    const bearing = gpsBearing(distance);

    if (isStale) {
      // A friend who went offline beside you: their last fix is minutes old and somewhere else, but Bluetooth
      // hears their phone now. The sighting wins (Turnstile test, 2026-10-14: stale GPS hid a friend at arm's length).
      if (bleNearby) {
        return { ...bleNearby, gpsLastKnownAt };
      }
      return {
        kind: 'gps-stale',
        displayName,
        distanceMeters: distance,
        listPreview: listPreviewForKind(
          'gps-stale',
          peer.peerId,
          distance,
          gpsLastKnownAt,
          safeNow,
          horizontalAccuracyMeters,
        ),
        sortTier: 2,
        sortDistance: distance ?? Number.POSITIVE_INFINITY,
        gpsLastKnownAt,
        horizontalAccuracyMeters,
        ...bearing,
      };
    }

    return {
      kind: 'gps-live',
      displayName,
      distanceMeters: distance,
      listPreview: listPreviewForKind(
        'gps-live',
        peer.peerId,
        distance,
        gpsLastKnownAt,
        safeNow,
        horizontalAccuracyMeters,
      ),
      sortTier: 1,
      sortDistance: distance ?? Number.POSITIVE_INFINITY,
      gpsLastKnownAt,
      horizontalAccuracyMeters,
      ...bearing,
    };
  }

  if (bleNearby) {
    return bleNearby;
  }

  return {
    kind: 'none',
    displayName,
    distanceMeters: null,
    listPreview: null,
    sortTier: 4,
    sortDistance: Number.POSITIVE_INFINITY,
    gpsLastKnownAt: null,
    horizontalAccuracyMeters: null,
    bearingDeg: null,
    bearingSource: null,
    bearingUncertaintyDeg: null,
  };
}

export function sortPeersByDisplay(
  peers: NearbyPeer[],
  displayById: Map<string, PeerDisplayInfo>,
): NearbyPeer[] {
  return [...peers].sort((a, b) => {
    const da = displayById.get(a.peerId);
    const db = displayById.get(b.peerId);
    const tierA = da?.sortTier ?? 4;
    const tierB = db?.sortTier ?? 4;
    if (tierA !== tierB) {
      return tierA - tierB;
    }
    const distA = da?.sortDistance ?? Number.POSITIVE_INFINITY;
    const distB = db?.sortDistance ?? Number.POSITIVE_INFINITY;
    if (distA !== distB) {
      return distA - distB;
    }
    return a.peerId.localeCompare(b.peerId);
  });
}

export function trackedPeerLabel(
  kind: PeerPresenceKind,
  distanceMeters: number | null,
  options: {
    peerId?: string;
    horizontalAccuracyMeters?: number | null;
  } = {},
): string {
  switch (kind) {
    case 'uwb-live':
      return distanceMeters !== null && Number.isFinite(distanceMeters)
        ? formatFindPeerDistanceWithWalkTime(distanceMeters, {
            peerId: options.peerId,
            uwbPrecise: true,
            horizontalAccuracyMeters: options.horizontalAccuracyMeters,
          })
        : '—';
    case 'gps-live':
    case 'gps-stale':
      return distanceMeters !== null && Number.isFinite(distanceMeters)
        ? formatFindPeerDistanceWithWalkTime(distanceMeters, {
            peerId: options.peerId,
            horizontalAccuracyMeters: options.horizontalAccuracyMeters,
          })
        : '—';
    case 'ble-in-range':
      return bleNearbyLabel(distanceMeters);
    default:
      return '—';
  }
}
